import React, { useEffect } from 'react';
import { Dispatch, SetStateAction, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { DepositStepsEnum } from './types';
import { Button, ErrorToast } from '../../../components';
import { SelectedAddress } from '../../../styles/stylesStyled';
import { InputBackOffice } from '../../../components/common/InputBackoffice';
import { CaptionText, P1, P2, P3 } from '../../../styles/FontStyles';
import { colors } from '../../../styles/GlobalStyles';
import { Flexed, SelectedUSDT } from '../../../styles/GlobalComponents';
import { TokensType, WalletsType } from '../../../types/types';
import {
  copyLink,
  formatUSD,
  getAkashaWalletBalanceFromBase,
  getAkashaWalletBalanceFromUltron,
  getULXWalletBalance
} from '../../../helpers/helperFunctions';
import { useTranslation } from 'react-i18next';
import walletImage from '../../../assets/icons/backOffice/Packages/wallet.svg';
import { depositAkasha } from '../../Marketplace/Marketplace.actions';
import { useParams } from 'react-router-dom';
import Spinner from '../../../components/common/Spinner';
import usdtIcon from '../../../assets/icons/backOffice/USDT.svg';
import wULXIcon from '../../../assets/icons/backOffice/ULX.svg';
import akashaIcon from '../../../assets/icons/backOffice/AKASHA.svg';
import { SelectNetwork } from './SelectNetwork/SelectNetwork';
import { ethers, Contract } from 'ethers';
import erc20Abi from '../../../assets/abi/erc20.json';

interface Step1Props {
  setDepositStep: Dispatch<SetStateAction<DepositStepsEnum>>;
  wallet: string;
  token: any;
  ulx: string;
  usdt: string;
  setUlx: Dispatch<SetStateAction<string>>;
  setUsdt: Dispatch<SetStateAction<string>>;
  selectedNetwork: WalletsType;
  setSelectedNetwork: Dispatch<SetStateAction<WalletsType>>;
  setBaseAkashaBalance: Dispatch<SetStateAction<number | undefined>>;
  baseAkashaBalance: number | undefined;
  ultronWUlxBalance: string | undefined;
}

export const Step1 = ({
  setDepositStep,
  wallet,
  token,
  ulx,
  usdt,
  setUlx,
  setUsdt,
  selectedNetwork,
  setSelectedNetwork,
  setBaseAkashaBalance,
  baseAkashaBalance,
  ultronWUlxBalance
}: Step1Props) => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation('packages');

  const { userPackage, exchangeRates, walletSummary, loading } = useAppSelector(
    (state) => state.marketplace
  );

  const getUltronAkashaBalance = async () => {
    const akashaBalance = await getAkashaWalletBalanceFromUltron(
      walletSummary.userAddress,
      process.env.REACT_APP_AKASHA_ADDRESS!,
      process.env.REACT_APP_WULX_DECIMALS!
    );

    setBaseAkashaBalance(akashaBalance);
  };

  const getBaseAkashaBalance = async () => {
    const akashaBalanceBase = await getAkashaWalletBalanceFromBase(
      walletSummary?.userAddress,
      process.env.REACT_APP_AKASHA_ADDRESS_FROM_BASE!,
      process.env.REACT_APP_WULX_DECIMALS!
    );

    setBaseAkashaBalance(akashaBalanceBase);
  };

  const truncateToTwoDecimals = (num: number) => {
    return Math.floor(num * 100) / 100;
  };

  const { id } = useParams<{ id: string }>();

  const handleDeposit = () => {
    if (token === 'wULX' || token === 'Ultron (wULX)') {
      if (Number(ulx) === 0 || Number(ulx) > Number(ultronWUlxBalance)) {
        ErrorToast('You don’t have enough balance.');
      } else {
        dispatch(
          depositAkasha({
            userPackageId: Number(id),
            amount: Number(ulx),
            token: token === 'wULX' || token === 'Ultron (wULX)' ? 'wulx' : '',
            chain: 'ultron',
            wallet: walletSummary.userAddress,
            setStep: setDepositStep
          })
        );
      }
    } else {
      if (Number(ulx) === 0 || Number(ulx) > baseAkashaBalance!) {
        ErrorToast('You don’t have enough balance.');
      } else {
        dispatch(
          depositAkasha({
            userPackageId: Number(id),
            amount: Number(ulx),
            token:
              token === 'AK1111' || token === 'Akasha (AK1111)' ? 'akasha' : '',
            chain: selectedNetwork?.networkType === 4 ? 'base' : 'ultron',
            wallet: walletSummary.userAddress,
            setStep: setDepositStep
          })
        );
      }
    }
  };

  const handleUlxChange = (e: any) => {
    const val = e;
    setUlx(val);

    if (!isNaN(parseFloat(val))) {
      const result =
        parseFloat(val) *
        (token === 'wULX' || token === 'Ultron (wULX)'
          ? exchangeRates?.ulxExchangeRate
          : selectedNetwork?.networkType === 4
          ? exchangeRates?.akashaBaseExchangeRate
          : exchangeRates?.akashaUltronExchangeRate);

      setUsdt(truncateToTwoDecimals(result).toString());
    } else {
      setUsdt('');
    }
  };

  const handleUsdtChange = (e: any) => {
    const val = e;
    setUsdt(val);

    if (!isNaN(parseFloat(val))) {
      const result =
        parseFloat(val) /
        (token === 'wULX' || token === 'Ultron (wULX)'
          ? exchangeRates?.ulxExchangeRate
          : selectedNetwork?.networkType === 4
          ? exchangeRates?.akashaBaseExchangeRate
          : exchangeRates?.akashaUltronExchangeRate);

      setUlx(truncateToTwoDecimals(result).toString());
    } else {
      setUlx('');
    }
  };

  const calculateDepositAmountRemaining = (
    maxDepositAmount: number,
    depositedAmount: number
  ) => {
    const depositedAmountRemaining = maxDepositAmount - depositedAmount;

    const truncated = depositedAmountRemaining;

    const newTruncated = Math.floor(truncated * 100) / 100;

    return newTruncated;
  };

  const calculatewUSDTExchangeRate = () => {
    const exchange =
      (token === 'wULX' || token === 'Ultron (wULX)'
        ? Number(ultronWUlxBalance)
        : selectedNetwork?.networkType === 3
        ? walletSummary?.availableBalanceAkasha
        : baseAkashaBalance) *
      (token === 'wULX' || token === 'Ultron (wULX)'
        ? exchangeRates?.ulxExchangeRate
        : selectedNetwork?.networkType === 4
        ? exchangeRates?.akashaBaseExchangeRate
        : exchangeRates?.akashaUltronExchangeRate);

    return formatUSD(exchange);
  };

  const checkMaxAmount = () => {
    const maxDeposit = userPackage?.package?.maxDepositAmount ?? 0;
    const depositedAmount = userPackage?.depositedUsdtAmount ?? 0;

    const depositedAmountRemaining = maxDeposit - depositedAmount;

    if (Number(usdt) > depositedAmountRemaining) {
      return true;
    } else {
      return false;
    }
  };

  const showCurrentDepositAmount = (selectedNetwork: any) => {
    if (selectedNetwork || token === 'wULX' || token === 'Ultron (wULX)') {
      return (
        <CaptionText color={colors.neutral500} mb='20'>
          Current Deposited Amount:{' '}
          {formatUSD(userPackage?.depositedUsdtAmount)} USDT /{' '}
          {formatUSD(userPackage?.depositedCurrencyAmount)}{' '}
          {userPackage?.depositedTokenCurrency}
        </CaptionText>
      );
    }
  };

  const showDepositAmount = (selectedNetwork: any) => {
    if (selectedNetwork || token === 'wULX' || token === 'Ultron (wULX)') {
      return (
        <>
          <Flexed between center mb='8'>
            <P3 bold>Deposit Amount</P3>
            <Button
              textButton
              w='auto'
              h='auto'
              onClick={() => setDepositStep(DepositStepsEnum.SELECT_PAYMENT)}>
              Add{' '}
              {`${
                token === 'wULX' || token === 'Ultron (wULX)'
                  ? 'wULX'
                  : 'AK1111'
              }`}
            </Button>
          </Flexed>
          <Flexed gap='10px'>
            <InputBackOffice
              value={ulx}
              type='number'
              onChange={handleUlxChange}
              icon={`${
                token === 'wULX' || token === 'Ultron (wULX)'
                  ? wULXIcon
                  : akashaIcon
              }`}
            />
            <InputBackOffice
              type='number'
              value={usdt}
              onChange={handleUsdtChange}
              icon={usdtIcon}
            />
          </Flexed>
          <Flexed between>
            <CaptionText color={colors.neutral500} mb='20'>
              {token === 'wULX' || token === 'Ultron (wULX)'
                ? 'wULX'
                : 'AK1111'}{' '}
              current balance:{' '}
              {token === 'wULX' || token === 'Ultron (wULX)'
                ? formatUSD(Number(ultronWUlxBalance))
                : formatUSD(baseAkashaBalance)}{' '}
              / {calculatewUSDTExchangeRate()} USDT
            </CaptionText>
            {selectedNetwork?.networkType === 4 && (
              <CaptionText color={colors.neutral500} mb='20'>
                Deposit fee 0.6%
              </CaptionText>
            )}
          </Flexed>
        </>
      );
    }
  };

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <>
          {' '}
          <P2 center mb='20' color={colors.neutral500}>
            NODE ID #{id}
          </P2>
          <P3 bold mb='8'>
            My Back Office Wallet Address
          </P3>
          <SelectedUSDT
            onClick={() => {
              copyLink(wallet, t('waiting.copied'));
            }}
            style={{ cursor: 'pointer' }}>
            <div className='truncated'>
              <div>{wallet}</div>
            </div>

            <img
              src={walletImage}
              alt='withdraw'
              style={{ width: 16, marginLeft: 10 }}
            />
          </SelectedUSDT>
          <CaptionText color={colors.error500} mb='20' mt='10'>
            Please make sure your connected wallet has enough {token} to
            complete the deposit. If not, click here to watch the tutorial on
            how to get {token}.
          </CaptionText>
          <P3 bold mb='8'>
            Node Type
          </P3>
          <InputBackOffice disabled value={userPackage?.package?.title} />
          <CaptionText color={colors.neutral500} mb='20'>
            Basic Node Max Deposit Amount:{' '}
            {formatUSD(userPackage?.package?.maxDepositAmount)} USDT
          </CaptionText>
          <P3 bold mb='8'>
            Coin Selected
          </P3>
          <InputBackOffice disabled value={token} />
          {token === 'AK1111' && (
            <>
              {' '}
              <P3 bold mb='8'>
                Blockchain
              </P3>
              <SelectNetwork
                onChange={(n) => {
                  setSelectedNetwork(n);
                  n?.networkType === 4
                    ? getBaseAkashaBalance()
                    : getUltronAkashaBalance();
                }}
                value={selectedNetwork}
              />
              {selectedNetwork?.networkType === 4 && (
                <CaptionText color={colors.neutral500} mb='20'>
                  Deposit fee 0.6%
                </CaptionText>
              )}
            </>
          )}
          {token === 'Akasha (AK1111)' && (
            <>
              {' '}
              <P3 bold mb='8'>
                Blockchain
              </P3>
              <SelectNetwork
                onChange={(n) => {
                  setSelectedNetwork(n);
                  n?.networkType === 4
                    ? getBaseAkashaBalance()
                    : getUltronAkashaBalance();
                }}
                value={selectedNetwork}
              />
              {selectedNetwork?.networkType === 4 && (
                <CaptionText color={colors.neutral500} mb='20'>
                  Deposit fee 0.6%
                </CaptionText>
              )}
            </>
          )}
          <P3 bold mb='8'>
            Deposit Amount Remaining
          </P3>
          <InputBackOffice
            disabled
            value={calculateDepositAmountRemaining(
              userPackage?.package?.maxDepositAmount ?? 0,
              userPackage?.depositedUsdtAmount ?? 0
            )}
            icon={usdtIcon}
          />
          {showCurrentDepositAmount(selectedNetwork)}
          {showDepositAmount(selectedNetwork)}
          <CaptionText color={colors.error500} mb='20' bold>
            Due to market price fluctuations, the value of your deposit may
            change over time. These changes are natural to market dynamics and
            cannot be controlled or guaranteed.
          </CaptionText>
          <Button
            mt='20'
            ml='10'
            onClick={handleDeposit}
            disabled={ulx === '' || checkMaxAmount()}>
            Deposit
          </Button>
        </>
      )}
    </>
  );
};
